import { useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { z } from 'zod'

/**
 * A document page opened for one action (`/invoices/…?action=post`): the state-aware row actions
 * of the customer profile and the credit drawer land on the document with that action's dialog
 * already open, so the confirmation, its checks and its errors live in one place (the document
 * page) and the user confirms with the document in view.
 */
export const DOCUMENT_ACTIONS = [
  'post',
  'delete',
  'void',
  'allocate',
  'convert',
  'share',
  'approve',
  'reject',
  'lost',
  // The invoice's issued PDF in English or Arabic (W5): issued and downloaded as the page opens.
  'pdf-en',
  'pdf-ar',
  // Send the invoice by e-mail (W4).
  'email',
] as const
export type DocumentAction = (typeof DOCUMENT_ACTIONS)[number]

export const documentActionSearchSchema = z.object({
  action: z.enum(DOCUMENT_ACTIONS).optional().catch(undefined),
})

/**
 * The action the page was opened for, read once, if the document's state and the user's
 * permissions allow it now (a stale link opens the page without a dialog). The parameter is then
 * removed from the URL, so a reload or Back doesn't open the dialog again.
 */
export function useRequestedAction<A extends DocumentAction>(allowed: Partial<Record<A, boolean>>): A | null {
  const search = useSearch({ strict: false }) as { action?: DocumentAction }
  const navigate = useNavigate()
  const [requested] = useState<A | null>(() => {
    const action = search.action as A | undefined
    return action && allowed[action] ? action : null
  })
  const present = search.action !== undefined
  useEffect(() => {
    if (!present) return
    void navigate({
      to: '.',
      search: (prev) => ({ ...prev, action: undefined }),
      replace: true,
    })
  }, [present, navigate])
  return requested
}
